/**
 * 检测当前页面是否在 iframe 中运行
 * @returns {boolean} 如果在 iframe 中返回 true，否则返回 false
 */
export function isInIframe(): boolean {
  try {
    // 方法1: 检查 window.self 和 window.top 是否相同
    if (window.self !== window.top) {
      return true
    }

    // 方法2: 检查 window.parent 是否存在且不等于 window.self
    if (window.parent && window.parent !== window.self) {
      return true
    }

    // 方法3: 检查 window.frameElement 是否存在
    if (window.frameElement) {
      return true
    }

    return false
  } catch (error) {
    // 如果访问 window.top 或 window.parent 时出现跨域错误，
    // 通常意味着页面在 iframe 中且与父页面不同源
    return true
  }
}

/**
 * 获取 iframe 的嵌套层级
 * @returns {number} 嵌套层级，0 表示不在 iframe 中
 */
export function getIframeDepth(): number {
  let depth = 0
  let currentWindow = window

  try {
    while (currentWindow.parent && currentWindow.parent !== currentWindow) {
      depth++
      currentWindow = currentWindow.parent

      // 防止无限循环，最大检测 10 层
      if (depth > 10) break
    }
  } catch (error) {
    // 跨域访问限制，至少有一层嵌套
    return depth > 0 ? depth : 1
  }

  return depth
}

/**
 * 检测是否可以与父窗口通信
 * @returns {boolean} 如果可以通信返回 true
 */
export function canCommunicateWithParent(): boolean {
  if (!isInIframe()) return false

  try {
    // 尝试访问父窗口的 location
    const parentOrigin = window.parent.location.origin
    return true
  } catch (error) {
    // 跨域限制，无法直接通信
    return false
  }
}

/**
 * 获取父窗口的信息（如果可访问）
 * @returns {object | null} 父窗口信息或 null
 */
export function getParentWindowInfo(): {
  origin?: string
  userAgent?: string
  referrer?: string
} | null {
  if (!isInIframe()) return null

  try {
    return {
      origin: window.parent.location.origin,
      userAgent: window.parent.navigator.userAgent,
      referrer: document.referrer,
    }
  } catch (error) {
    // 只能获取 referrer 信息
    return {
      referrer: document.referrer,
    }
  }
}

/**
 * 向父窗口发送消息
 * @param {any} message 要发送的消息
 * @param {string} targetOrigin 目标源，默认为 '*'
 */
export function postMessageToParent(
  message: any,
  targetOrigin: string = "*"
): void {
  if (!isInIframe()) {
    console.warn("Not in iframe, cannot send message to parent")
    return
  }

  try {
    window.parent.postMessage(message, targetOrigin)
  } catch (error) {
    console.error("Failed to send message to parent:", error)
  }
}

/**
 * 监听来自父窗口的消息
 * @param {function} callback 消息处理回调函数
 * @param {string} expectedOrigin 期望的消息来源，可选
 * @returns {function} 取消监听的函数
 */
export function listenToParentMessages(
  callback: (event: MessageEvent) => void,
  expectedOrigin?: string
): () => void {
  const messageHandler = (event: MessageEvent) => {
    // 如果指定了期望的来源，进行验证
    if (expectedOrigin && event.origin !== expectedOrigin) {
      return
    }

    callback(event)
  }

  window.addEventListener("message", messageHandler)

  // 返回取消监听的函数
  return () => {
    window.removeEventListener("message", messageHandler)
  }
}

/**
 * 检测特定的 iframe 嵌套场景
 */
export const iframeDetection = {
  /**
   * 检测是否在移动应用的 WebView 中
   */
  isInWebView(): boolean {
    const userAgent = navigator.userAgent.toLowerCase()
    return (
      /webview|wv\)|android.*version\/.*chrome|iphone.*mobile.*safari/i.test(
        userAgent
      ) && isInIframe()
    )
  },

  /**
   * 检测是否在第三方嵌入环境中（如 CodePen, JSFiddle 等）
   */
  isInThirdPartyEmbed(): boolean {
    if (!isInIframe()) return false

    const referrer = document.referrer.toLowerCase()
    const thirdPartyDomains = [
      "codepen.io",
      "jsfiddle.net",
      "codesandbox.io",
      "stackblitz.com",
      "replit.com",
      "glitch.com",
    ]

    return thirdPartyDomains.some((domain) => referrer.includes(domain))
  },

  /**
   * 检测是否在企业内部系统中嵌入
   */
  isInEnterpriseEmbed(): boolean {
    if (!isInIframe()) return false

    const referrer = document.referrer
    // 检测内网地址或企业域名模式
    return /^https?:\/\/(localhost|127\.0\.0\.1|192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.|.*\.local|.*\.corp|.*\.internal)/i.test(
      referrer
    )
  },
}
